/**
 * Skip Link Component
 *
 * Provides keyboard navigation skip link for accessibility
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

export interface SkipLinkProps {
  /**
   * Target ID to skip to
   */
  targetId: string;

  /**
   * Link label
   */
  label?: string;

  /**
   * Additional className
   */
  className?: string;
}

/**
 * Skip Link Component
 *
 * Provides a skip link for keyboard navigation (WCAG AA requirement)
 */
export function SkipLink({
  targetId,
  label = 'Skip to main content',
  className,
}: SkipLinkProps): React.JSX.Element {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.focus();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <a
      href={`#${targetId}`}
      onClick={handleClick}
      className={cn(
        'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50',
        'focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground',
        'focus:rounded-md focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring',
        className
      )}
      aria-label={label}
    >
      {label}
    </a>
  );
}
